import './UnderwritingDetailsController.scss';
import { Paper } from '@hometap/htco-components';
import { MultiPicker } from '../../components';
import { UnderwritingDetailsProps } from './types/types';
import UnderwriterBulletList from './components/UnderwriterBulletList';
import { FinalApproveButton } from './components/FinalApproveButton/FinalApproveButton';
import APP_REVIEW_STATUSES from 'data/constants/applicationReviewStatuses';
import useCurrentUser from 'hooks/useCurrentUser';
import { UseCurrentUser } from 'hooks/types/types';
import { CalculatorButton } from './components/CalculatorButton/CalculatorButton';
import { UnderwriterBlockingErrors } from './components/UnderwriterBlockingErrors/UnderwriterBlockingErrors';
import { getDealTermsLeftItems, getDealTermsRightItems, getOverviewItems } from './utils/underwritingItems';
import Callout from 'components/Callout/Callout';
import { currency } from 'utils/numbers';

const UnderwritingDetails = ({
  trackId,
  trackHistoryUrl,
  uwRuleVersion,
  applicationSpecialist,
  ownersEndingEquity,
  totalEndingEquity,
  underwriter,
  htLienPosition,
  principalPaydown,
  totalPaydown,
  maximumInvestmentAmount,
  beginningHomeValuationLabel,
  beginningHomeValuation,
  beginningHomeValuationExpStatus,
  lienBalanceTotal,
  pastDueLienBalanceTotal,
  currentIncomeWeightedRiskBand,
  finalCompositeFico,
  lossThreshold,
  shareUp,
  shareDown,
  trackReviewStatus,
  uwDscr,
  hasRentalResidentialType,
  rb6Criteria,
  highestSingleYearDelinquentTaxes,
  latestUwCalculatorOutput,
  refetch,
  applicationIsApprovedOrClosedLost,
  enabledFinalApprovalBlockingErrors,
  finalApprovalBlockingErrors,
  refetchFinalApprovalBlockingErrorsData,
  thirdLienCap,
}: UnderwritingDetailsProps) => {
  const { isInUnderwriterGroup, user } = useCurrentUser() as UseCurrentUser;

  const canShowApproveButton = trackReviewStatus === APP_REVIEW_STATUSES.uwApproved && isInUnderwriterGroup;
  const enableFieldEditing = isInUnderwriterGroup && !applicationIsApprovedOrClosedLost;

  const overviewItems = getOverviewItems({
    uwRuleVersion,
    applicationSpecialist,
    underwriter,
    htLienPosition,
    principalPaydown,
    totalPaydown,
    rb6Criteria,
    enableFieldEditing,
    trackId,
    highestSingleYearDelinquentTaxes,
    hasRentalResidentialType,
    uwDscr,
    user,
    refetchTrack: refetch,
    refetchBlockers: refetchFinalApprovalBlockingErrorsData,
  });

  const dealTermsLeftItems = getDealTermsLeftItems({
    maximumInvestmentAmount,
    beginningHomeValuation,
    beginningHomeValuationLabel,
    beginningHomeValuationExpStatus,
    lienBalanceTotal,
    pastDueLienBalanceTotal,
    ownersEndingEquity,
    totalEndingEquity,
  });

  const dealTermsRightItems = getDealTermsRightItems({
    lossThreshold,
    currentIncomeWeightedRiskBand,
    finalCompositeFico,
    shareUp,
    shareDown,
  });

  const onApproveSuccess = () => refetch?.();

  const errors = (latestUwCalculatorOutput?.allFailedCriteriaDetails ?? []).concat(finalApprovalBlockingErrors ?? []);
  const disabledApproveButton = errors?.length > 0;
  let thirdLienCapAppliedCallout = null;
  if (latestUwCalculatorOutput?.thirdLienCapApplied) {
    if (thirdLienCap) {
      thirdLienCapAppliedCallout = (
        <Callout type="warning">
          Max Investment amount set to <strong>{currency(thirdLienCap, true)}</strong> per Hometap's anticipated lien
          position.
        </Callout>
      );
    } else {
      // This is unexpected to happen, but could happen if we do some sort of data migration to remove the third lien cap on a track.
      // If it does happen, we want to alert the user and engineering that something is wrong. Look at the track's third_lien_cap and
      // compare to the latestUwCalculatorOutput.
      thirdLienCapAppliedCallout = (
        <Callout type="warning">
          Something went wrong. This track has a third lien cap applied, but is not expected to. Please reach out to
          engineering to investigate this discrepancy.
        </Callout>
      );
    }
  }

  return (
    <div className="ApplicationReviewSection ReadonlyUWWrapper">
      <Paper theme="plain" className="MultiPickerContainer ReadonlyUWMultipicker">
        <MultiPicker
          sectionTitle="Underwriting details"
          historyUrl={trackHistoryUrl}
          visibleSummary={false}
          pickList={[]}
          selectedId={0}
          sectionAlert={{}}
          onEdit={null}
        />
      </Paper>

      <Paper theme="plain" className="FormDetailsContainer UnderwritingDetailsContainer">
        <div className="UnderwritingDetails">
          {thirdLienCapAppliedCallout}
          <div className="Section">
            <h3>OVERVIEW</h3>
            <UnderwriterBulletList items={overviewItems} />
          </div>

          <div className="Section DealTermsSection">
            <h3>DEAL TERMS</h3>
            <div className="DealTermsContainer">
              <UnderwriterBulletList items={dealTermsLeftItems} />
              <UnderwriterBulletList items={dealTermsRightItems} />
            </div>
          </div>

          {(canShowApproveButton || isInUnderwriterGroup) && (
            <div className="UnderwritingDetailsButtons">
              <FinalApproveButton
                disabled={disabledApproveButton}
                visible={canShowApproveButton}
                onSuccess={onApproveSuccess}
              />
              {isInUnderwriterGroup && <CalculatorButton latestUwCalculatorOutput={latestUwCalculatorOutput} />}
            </div>
          )}

          {enabledFinalApprovalBlockingErrors && <UnderwriterBlockingErrors errors={errors} />}
        </div>
      </Paper>
    </div>
  );
};

export default UnderwritingDetails;
